"use client"

import { useState, useContext, useEffect } from "react"
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  InputAdornment,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Autocomplete,
  Collapse,
  Chip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import BusinessIcon from "@mui/icons-material/Business"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import HomeIcon from "@mui/icons-material/Home"
import ApartmentIcon from "@mui/icons-material/Apartment"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import { ErpContext } from "../../context/ErpContext.js"

const ModalCrearActualizarEntidad = ({ open, handleClose, entidad, idTipoEntidad }) => {
  const theme = useTheme()
  const { 
    postEntidad, 
    updateEntidad, 
    condicionIva, 
    getCondicionIva, 
    getCondicionIvaByEntidad, 
    isNavbarCollapsed,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
  } = useContext(ErpContext)

  // Estados
  const [entidadData, setEntidadData] = useState(entidad || {})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errors, setErrors] = useState({})
  const [idCondicionIvaEntidad, setIdCondicionIvaEntidad] = useState("")
  const [notification, setNotification] = useState({ open: false, message: "", severity: "error" })
  
  // Estados para atributos
  const [atributosForm, setAtributosForm] = useState([])
  const [expandedAtributos, setExpandedAtributos] = useState(true)
  const [atributosErrors, setAtributosErrors] = useState({})
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({}) // { idAtributo: [opciones] }
  const [atributosDisponibles, setAtributosDisponibles] = useState([]) // Estado local para atributos del tipo

  // Cargar opciones para cada atributo
  const cargarOpcionesAtributos = async (atributosParam = null, idTipoEntidadParam = null) => {
    const atributosAUsar = atributosParam || atributosDisponibles
    const idTipoEntidad = idTipoEntidadParam || entidadData?.idTipoEntidad || entidad?.idTipoEntidad
    
    if (!atributosAUsar || atributosAUsar.length === 0) {
      setOpcionesPorAtributo({})
      return
    }

    if (!idTipoEntidad) {
      console.warn('idTipoEntidad no disponible para cargar atributos')
      setOpcionesPorAtributo({})
      return
    }

    const opcionesMap = {}
    for (const attr of atributosAUsar) {
      const idAtributo = attr.idEntidadAtributo || attr.id
      try {
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(idAtributo, idTipoEntidad)
        opcionesMap[idAtributo] = opciones || []
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${idAtributo}:`, error)
        opcionesMap[idAtributo] = []
      }
    }
    setOpcionesPorAtributo(opcionesMap)
  }

  // Inicializar atributos cuando se cargan los atributos disponibles
  useEffect(() => {
    const inicializarAtributos = async () => {
      if (atributosDisponibles && atributosDisponibles.length > 0) {
        // Si es edición, inicializar con los valores actuales de la entidad
        if (entidad && entidad.id) {
          const inicial = atributosDisponibles.map((attr, index) => {
            // Buscar el valor del atributo en diferentes formatos posibles
            let valorAtributo = null
            const numAtributo = index + 1
            
            // Primero intentar con opcionAtributo (cuando viene del include)
            if (entidad[`opcionAtributo${numAtributo}`]?.id) {
              valorAtributo = entidad[`opcionAtributo${numAtributo}`].id
            }
            // Luego intentar con entidadDatoAtributo (valor directo)
            else if (entidad[`entidadDatoAtributo${numAtributo}`] !== undefined && 
                     entidad[`entidadDatoAtributo${numAtributo}`] !== null) {
              valorAtributo = entidad[`entidadDatoAtributo${numAtributo}`]
            }
            
            return {
              id: attr.idEntidadAtributo || attr.id, // Usar idEntidadAtributo si existe
              descripcion: attr.descripcion,
              valor: valorAtributo ? parseInt(valorAtributo) : null,
            }
          })
          setAtributosForm(inicial)
          setAtributosErrors({})
          setExpandedAtributos(true)
        } else {
          // Para nueva entidad, todos null
          const inicial = atributosDisponibles.map((attr) => ({
            id: attr.idEntidadAtributo || attr.id, // Usar idEntidadAtributo si existe
            descripcion: attr.descripcion,
            valor: null,
          }))
          setAtributosForm(inicial)
          setAtributosErrors({})
          setExpandedAtributos(true)
        }
      } else if (atributosDisponibles && atributosDisponibles.length === 0) {
        setAtributosForm([])
        setAtributosErrors({})
        setOpcionesPorAtributo({})
      }
    }
    
    inicializarAtributos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atributosDisponibles, entidad])

  // Recargar atributos cuando cambia el tipo de entidad
  useEffect(() => {
    const cargarAtributosPorTipo = async () => {
      if (entidadData?.idTipoEntidad) {
        // Cargar atributos específicos del tipo
        const atributos = await getEntidadAtributoNoEliminados(entidadData.idTipoEntidad)
        setAtributosDisponibles(atributos || [])
        
        // Cargar opciones de clasificación
        if (atributos && atributos.length > 0) {
          await cargarOpcionesAtributos(atributos, entidadData.idTipoEntidad)
        }
      }
    }
    
    cargarAtributosPorTipo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadData?.idTipoEntidad])

  // Inicializar datos cuando se abre el modal
  useEffect(() => {
    if (open) {
      setEntidadData(entidad || {})
      setErrors({})
      setError(null)
      setExpandedAtributos(true)
      setAtributosErrors({})
      
      // Cargar atributos si no están cargados y hay idTipoEntidad
      const idTipoEntidad = entidad?.idTipoEntidad
      if (idTipoEntidad && (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0)) {
        getEntidadAtributoNoEliminados(idTipoEntidad)
      }
      
      // Cargar condicionIva si no están cargadas
      if (condicionIva && condicionIva.length === 0) {
        getCondicionIva()
      }

      // Si es edición, cargar la condición IVA actual de la entidad
      if (entidad && entidad.id) {
        loadCondicionIvaForEntidad(entidad.id)
      } else {
        setIdCondicionIvaEntidad("")
      }
    }
  }, [open, entidad])

  const loadCondicionIvaForEntidad = async (idEntidad) => {
    try {
      const condicionIvaData = await getCondicionIvaByEntidad(idEntidad)
      if (condicionIvaData && condicionIvaData.idCondicionIva) {
        setIdCondicionIvaEntidad(condicionIvaData.idCondicionIva)
      } else {
        setIdCondicionIvaEntidad("")
      }
    } catch (error) {
      console.error("Error al cargar condición IVA:", error)
      setIdCondicionIvaEntidad("")
    }
  }

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target
    setEntidadData((prev) => ({ ...(prev || {}), [name]: value }))

    // Limpiar error del campo cuando se modifica
    if (errors && errors[name]) {
      setErrors({ ...errors, [name]: null })
    }
  }

  // Manejar cambio en atributos
  const handleAtributoFormChange = (id, valor) => {
    // Convertir string vacío a null
    const valorFinal = valor === "" || valor === null ? null : (typeof valor === 'string' ? parseInt(valor) || null : valor)
    setAtributosForm((prev) => prev.map((attr) => (attr.id === id ? { ...attr, valor: valorFinal } : attr)))
    setAtributosErrors((prev) => {
      if (!prev[id]) return prev
      const { [id]: _, ...rest } = prev
      return rest
    })
  }

  // Validar formulario
  const validateForm = () => {
    const newErrors = {}
    const newAtributosErrors = {}

    // Validar campos requeridos
    if (!entidadData || !entidadData.descripcion?.trim()) {
      newErrors.descripcion = "El nombre es requerido"
    }

    if (!entidadData || !entidadData.dniCuitCuil?.trim()) {
      newErrors.dniCuitCuil = "El DNI/CUIT/CUIL es requerido"
    }

    if (entidadData && entidadData.email && !/\S+@\S+\.\S+/.test(entidadData.email)) {
      newErrors.email = "Email inválido"
    }

    if (!entidadData?.idTipoEntidad && !idTipoEntidad) {
      newErrors.idTipoEntidad = "Debe seleccionar un tipo de entidad"
    }

    if (atributosForm && atributosForm.length > 0) {
      atributosForm.forEach((attr) => {
        if (attr.valor === null || attr.valor === undefined) {
          newAtributosErrors[attr.id] = "Selecciona un valor para este atributo"
        }
      })
    }

    setErrors(newErrors)
    setAtributosErrors(newAtributosErrors)
    return Object.keys(newErrors).length === 0 && Object.keys(newAtributosErrors).length === 0
  }

  // Guardar cambios
  const handleSave = async () => {
    if (!validateForm()) return

    setLoading(true)
    setError(null)

    try {
      if (entidadData && entidadData.id) {
        // Validar que no se intente actualizar entidades genéricas
        if (entidadData.id === 1 || entidadData.id === 2) {
          setNotification({
            open: true,
            message: "No se puede actualizar entidades genéricas",
            severity: "warning",
          });
          setLoading(false);
          setError("No se puede actualizar entidades genéricas");
          return;
        }

        // Si la entidad ya tiene ID -> ACTUALIZAR
        await updateEntidad(
          entidadData.id,
          entidadData.descripcion || "",
          entidadData.apellido || "",
          entidadData.telefono || "",
          entidadData.email || "",
          entidadData.dniCuitCuil || "",
          entidadData.direccion || "",
          entidadData.localidad || "",
          entidadData.provincia || "",
          idTipoEntidad,
          idCondicionIvaEntidad,
          atributosForm,
        )
      } else {
        // Si la entidad NO tiene ID -> CREAR
        await postEntidad(
          entidadData?.descripcion || "",
          entidadData?.apellido || "",
          entidadData?.telefono || "",
          entidadData?.email || "",
          entidadData?.dniCuitCuil || "",
          entidadData?.direccion || "",
          entidadData?.localidad || "",
          entidadData?.provincia || "",
          idTipoEntidad,
          idCondicionIvaEntidad,
          atributosForm,
        )
      }

      handleClose(true) // Cierra el modal y actualiza la tabla
    } catch (err) {
      console.error("Error al guardar entidad:", err)
      
      // Mostrar mensaje de error en Snackbar
      setNotification({
        open: true,
        message: err.message || "Ocurrió un error al guardar los datos. Intente nuevamente.",
        severity: "error",
      });
      
      // Mostrar mensaje de error específico del backend o mensaje genérico
      if (err.message) {
        setError(err.message)
      } else {
        setError("Ocurrió un error al guardar los datos. Intente nuevamente.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={() => !loading && handleClose(false)} closeAfterTransition>
      <Box>
        <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 600, md: 700 },
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Encabezado */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              pl: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
  <BusinessIcon
    sx={{
      fontSize: 24,
      color: 'rgb(244, 147, 14)',
      mr: 1.5,
    }}
  />
  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
    {`${entidadData?.id ? "Editar" : "Crear Nuevo"} ${
      idTipoEntidad === 1
        ? "Cliente"
        : idTipoEntidad === 2
        ? "Proveedor"
        : "Entidad"
    }`}
  </Typography>
</Box>

            <IconButton
              onClick={() => handleClose(false)}
              disabled={loading}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Contenido del formulario */}
          <Box
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 130px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.text.primary, 0.05),
              },
            }}
          >
            {/* Mensaje de error */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Sección: Información Personal */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <PersonIcon sx={{ color: 'rgb(244, 147, 14)',mr: 1 }} />
                  Información Personal
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre / Razón Social"
                  name="descripcion"
                  value={entidadData?.descripcion || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.descripcion}
                  helperText={errors.descripcion}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Apellido"
                  name="apellido"
                  value={entidadData?.apellido || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="DNI / CUIT / CUIL"
                  name="dniCuitCuil"
                  value={entidadData?.dniCuitCuil || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.dniCuitCuil}
                  helperText={errors.dniCuitCuil}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <InputLabel>Condición IVA</InputLabel>
                  <Select
                    value={idCondicionIvaEntidad}
                    onChange={(e) => setIdCondicionIvaEntidad(e.target.value)}
                    label="Condición IVA"
                  >
                    <MenuItem value="">
                      <em>Sin seleccionar</em>
                    </MenuItem>
                    {condicionIva.map((condicion) => (
                      <MenuItem key={condicion.id} value={condicion.id}>
                        {condicion.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Sección: Información de Contacto */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <PhoneIcon sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Información de Contacto
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  name="telefono"
                  value={entidadData?.telefono || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={entidadData?.email || ""}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Sección: Dirección */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <LocationOnIcon  sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Dirección
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  name="direccion"
                  value={entidadData?.direccion || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Localidad"
                  name="localidad"
                  value={entidadData?.localidad || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Provincia"
                  name="provincia"
                  value={entidadData?.provincia || ""}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Sección: Atributos de la Entidad */}
              {atributosForm && atributosForm.length > 0 && (
                <>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "default",
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Divider sx={{ flex: 1, maxWidth: 200 }}>
                          <Chip 
                            label="Atributos de la Entidad" 
                            variant="outlined" 
                            sx={{ 
                              fontWeight: 500, 
                              color: '#5b5b5f',
                              border: '1px solid #5b5b5f',
                            }} 
                          />
                        </Divider>
                      </Box>
                      <IconButton size="small" disabled>
                        <ExpandLessIcon />
                      </IconButton>
                    </Box>
                    <Collapse in={expandedAtributos} timeout="auto" unmountOnExit={false}>
                      <Grid container spacing={2} sx={{ mt: 1, p: 2 }}>
                        {atributosForm.map((attr) => {
                          const opciones = opcionesPorAtributo[attr.id] || []
                          const attrError = atributosErrors[attr.id]
                          return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={attr.id}>
                              <Autocomplete
                                size="small"
                                disablePortal
                                options={opciones}
                                getOptionLabel={(option) => option.descripcion || ""}
                                value={opciones.find(o => o.id === attr.valor) || null}
                                onChange={(event, newValue) => {
                                  handleAtributoFormChange(attr.id, newValue ? newValue.id : null)
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={attr.descripcion}
                                    required
                                    error={!!attrError}
                                    helperText={attrError}
                                    sx={{
                                      borderRadius: 2,
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "#5b5b5f",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "#5b5b5f",
                                        },
                                      },
                                      "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#5b5b5f",
                                      },
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Collapse>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Footer con botones */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleClose(false)}
              disabled={loading}
              sx={{
                mr: 2,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minWidth: 120,
                boxShadow: 2,
              }}
            >
              {loading ? "Guardando..." : entidadData?.id ? "Actualizar" : "Crear"}
            </Button>
          </Box>
        </Box>
        </Fade>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            variant="filled"
            sx={{
              width: "100%",
              "& .MuiAlert-icon": {
                fontSize: "1.5rem",
              },
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  )
}

export default ModalCrearActualizarEntidad

